/**
 * Parser tolerante do campo `detail` de workout items + helper de URL de GIF.
 *
 * Portado de `frontend/lib/workout-detail-parser.ts` e `frontend/lib/gif-url.ts`
 * (legado Next). Vive em `components/exec/` (não em `lib/`) por escopo desta
 * task — a fundação `lib/*` é intocável. Sem mudança de contrato.
 *
 * Formato canônico do detail:
 *   "3x10 @ 60kg | descanso 90s | drop-set | obs: ombro doendo"
 * É tolerante: campos fora de ordem, ausentes, ou texto livre.
 */

import { env } from "@/lib/env";

export interface ParsedWorkoutDetail {
  sets: number | null;
  reps: string | null;
  load_kg: number | null;
  rest_seconds: number | null;
  technique: string | null;
  notes: string | null;
  raw: string;
}

const EMPTY: Omit<ParsedWorkoutDetail, "raw"> = {
  sets: null,
  reps: null,
  load_kg: null,
  rest_seconds: null,
  technique: null,
  notes: null,
};

const SETS_REGEX = /^\s*(\d+)x([\d-]+|\?)\s*$/i;
const LOAD_REGEX = /^\s*@\s*([\d.,]+)\s*kg\s*$/i;
const REST_REGEX = /^\s*descanso\s+(\d+)\s*s\s*$/i;
const NOTES_REGEX = /^\s*obs:\s*(.+)$/i;

const INLINE_SETS_REGEX = /(\d+)\s*[xX×]\s*([\d-]+|\?)/;
const INLINE_LOAD_REGEX = /@\s*([\d.,]+)\s*kg|(\d[\d.,]*)\s*kg(?!\w)/i;
const INLINE_REST_REGEX = /descanso\s+(\d+)\s*s|(\d+)\s*s\s*(?:de\s+)?(?:descanso|rest)/i;
const INLINE_NOTES_REGEX = /\bobs[:.]?\s*(.+)$/i;

export function parseWorkoutDetail(detail: string | null | undefined): ParsedWorkoutDetail {
  const raw = detail ?? "";
  if (!raw.trim()) return { ...EMPTY, raw };

  const parts = raw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  const out: ParsedWorkoutDetail = { ...EMPTY, raw };
  const unknownParts: string[] = [];

  for (const part of parts) {
    const setsMatch = SETS_REGEX.exec(part);
    if (setsMatch) {
      const sets = parseInt(setsMatch[1], 10);
      out.sets = Number.isFinite(sets) ? sets : null;
      out.reps = setsMatch[2] === "?" ? null : setsMatch[2];
      continue;
    }
    const loadMatch = LOAD_REGEX.exec(part);
    if (loadMatch) {
      const load = parseFloat(loadMatch[1].replace(",", "."));
      out.load_kg = Number.isFinite(load) ? load : null;
      continue;
    }
    const restMatch = REST_REGEX.exec(part);
    if (restMatch) {
      const rest = parseInt(restMatch[1], 10);
      out.rest_seconds = Number.isFinite(rest) ? rest : null;
      continue;
    }
    const notesMatch = NOTES_REGEX.exec(part);
    if (notesMatch) {
      out.notes = notesMatch[1].trim();
      continue;
    }

    const consumed = consumeInline(part, out);
    if (consumed.remaining.trim()) unknownParts.push(consumed.remaining.trim());
  }

  if (unknownParts.length > 0) {
    out.technique = unknownParts
      .map((p) => p.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join(" | ");
  }

  return out;
}

function consumeInline(part: string, out: ParsedWorkoutDetail): { remaining: string } {
  let remaining = part;

  if (out.notes == null) {
    const m = INLINE_NOTES_REGEX.exec(remaining);
    if (m) {
      out.notes = m[1].trim();
      remaining = remaining.slice(0, m.index).trim();
    }
  }

  if (out.rest_seconds == null) {
    const m = INLINE_REST_REGEX.exec(remaining);
    if (m) {
      const n = parseInt(m[1] ?? m[2], 10);
      if (Number.isFinite(n)) out.rest_seconds = n;
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).trim();
    }
  }

  if (out.load_kg == null) {
    const m = INLINE_LOAD_REGEX.exec(remaining);
    if (m) {
      const rawLoad = (m[1] ?? m[2] ?? "").replace(",", ".");
      const n = parseFloat(rawLoad);
      if (Number.isFinite(n)) out.load_kg = n;
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).trim();
    }
  }

  if (out.sets == null && out.reps == null) {
    const m = INLINE_SETS_REGEX.exec(remaining);
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n)) out.sets = n;
      out.reps = m[2] === "?" ? null : m[2];
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).trim();
    }
  }

  return { remaining };
}

/** Heurística pra inferir nº de séries (pre-popular inputs do modal). */
export function guessSetsFromDetail(detail: string | null | undefined, fallback = 3): number {
  if (!detail) return fallback;
  const parsed = parseWorkoutDetail(detail);
  if (parsed.sets != null && parsed.sets > 0 && parsed.sets <= 10) return parsed.sets;
  const m = detail.match(/(\d{1,2})\s*[xX×]\s*\d/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n > 0 && n <= 10) return n;
  }
  const m2 = detail.match(/(\d{1,2})\s*séri/i);
  if (m2) {
    const n = parseInt(m2[1], 10);
    if (n > 0 && n <= 10) return n;
  }
  return fallback;
}

export function hasStructuredFields(parsed: ParsedWorkoutDetail): boolean {
  return (
    parsed.sets != null ||
    parsed.reps != null ||
    parsed.load_kg != null ||
    parsed.rest_seconds != null ||
    !!parsed.technique ||
    !!parsed.notes
  );
}

export function formatSetsReps(sets: number | null, reps: string | null): string | null {
  if (sets != null && reps) return `${sets} ${sets === 1 ? "série" : "séries"} × ${reps} reps`;
  if (sets != null) return `${sets} ${sets === 1 ? "série" : "séries"}`;
  if (reps) return `${reps} reps`;
  return null;
}

export function formatRest(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (rem === 0) return `${mins} min`;
  return `${mins}min ${rem}s`;
}

export function formatLoad(loadKg: number | null): string | null {
  if (loadKg == null || loadKg <= 0) return null;
  const formatted = Number.isInteger(loadKg)
    ? loadKg.toString()
    : loadKg.toFixed(1).replace(/\.0$/, "");
  return `${formatted} kg`;
}

/**
 * URL pública de GIF no Supabase Storage. Buckets coexistem:
 *  - `gifs` (novo, flat): path = filename. Sem `/`.
 *  - `exercicios` (legado): path com subpasta por grupo. Com `/`.
 * URL absoluta volta como veio. Base vem de `env.supabaseUrl` (Vite).
 */
export function gifPublicUrl(gifPath: string | null | undefined): string | null {
  if (!gifPath) return null;
  if (/^https?:\/\//i.test(gifPath)) return gifPath;
  const base = env.supabaseUrl.replace(/\/$/, "");
  const bucket = gifPath.includes("/") ? "exercicios" : "gifs";
  return `${base}/storage/v1/object/public/${bucket}/${gifPath
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;
}
